import { cva } from 'class-variance-authority';
import { Alert, Button, Flex, Modal, Text } from '@nula/core-ui';

import styles from './accessibility-explainer.module.css';
import type { AccessibilityPermissionFlow } from '../hooks/use-accessibility-permission-flow';

export interface AccessibilityExplainerProps {
  open: boolean;
  /** The permission flow instance owned by the parent — passed in to ensure single-instance state. */
  flow: AccessibilityPermissionFlow;
  onClose: () => void;
  onGranted: () => void;
}

const root = cva(styles.root);

export function AccessibilityExplainer({
  open,
  flow,
  onClose,
  onGranted,
}: AccessibilityExplainerProps) {
  const { request } = flow;

  const handleOpenSettings = async () => {
    const result = await request();
    if (result === 'granted') onGranted();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Spot meetings as you join them"
      footer={null}
      closable={false}
      centered
    >
      <Flex vertical gap="lg" className={root()}>
        <Flex vertical gap="md">
          <Text>
            Nula can notice when you are in a meeting in your browser — Google
            Meet, Zoom, Teams — and offer to take notes for you.
          </Text>
          <Text>
            To do this, macOS needs your permission to let Nula see your active
            tab&apos;s URL. That&apos;s all Nula reads — not page text, not tab
            content, not anything else.
          </Text>
          <Alert
            type="info"
            showIcon
            message="Restart Nula after granting"
            description="macOS only recognises the new permission on a fresh launch. After you grant access in System Settings, quit and reopen Nula once."
          />
        </Flex>
        <Flex justify="end" gap="sm">
          <Button variant="default" onClick={onClose}>
            Not now
          </Button>
          <Button variant="primary" onClick={handleOpenSettings}>
            Open System Settings
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
